import { Component, OnDestroy, OnInit } from '@angular/core'
import { Router, RouterLink } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SvgIconComponent } from 'angular-svg-icon'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import {
  exhaustMap,
  interval,
  last,
  Observable,
  startWith,
  Subscription,
  takeWhile,
  tap,
} from 'rxjs'

import { BackButtonComponent } from '../../components/back-button/back-button.component'
import { BooleanComponent } from '../../components/boolean/boolean.component'
import { BranchDetailBaseAbstract } from '../../components/branch-detail-base/branch-detail-base.abstract'
import { BranchDetailBaseComponent } from '../../components/branch-detail-base/branch-detail-base.component'
import { ButtonComponent } from '../../components/button/button.component'
import { PageHeadlineComponent } from '../../components/page-headline/page-headline.component'
import { TypedTemplateDirective } from '../../directives/typed-template.directive'
import {
  AddressListResponseMeta,
  ValidatedAddress,
} from '../../models/address.model'
import {
  ADDRESS_VALIDATION_IN_PROGRESS_STATES,
  Branch,
  BranchProgressState,
  BranchStatus,
} from '../../models/branch.model'
import { AddressRepository } from '../../repositories/address/address.repository'
import { ListResponse } from '../../repositories/base.repository'
import {
  BranchRepository,
  BranchStateResponse,
} from '../../repositories/branch/branch.repository'

@Component({
  selector: 'app-clearing-detail',
  standalone: true,
  imports: [
    ButtonComponent,
    SvgIconComponent,
    RouterLink,
    TranslatePipe,
    TypedTemplateDirective,
    PageHeadlineComponent,
    BranchDetailBaseComponent,
    BackButtonComponent,
    BooleanComponent,
  ],
  templateUrl: './clearing-detail.component.html',
})
export class ClearingDetailComponent
  extends BranchDetailBaseAbstract
  implements OnInit, OnDestroy
{
  private branchSubscription!: Subscription
  private addressesSubscription!: Subscription
  public branch?: Branch
  public openConflicts?: number = undefined

  private syncSubscription?: Subscription
  public isValidating = false

  public constructor(
    private readonly branchRepository: BranchRepository,
    private readonly addressRepository: AddressRepository,
    private readonly translateService: TranslateService,
    private readonly notificationService: NzNotificationService,
    private readonly router: Router
  ) {
    super()
  }

  public ngOnInit(): void {
    this.fetchBranchDetails()
    this.addressesSubscription = this.fetchIsResolutedFlag().subscribe()
  }

  public ngOnDestroy(): void {
    this.branchSubscription?.unsubscribe()
    this.syncSubscription?.unsubscribe()
    this.addressesSubscription?.unsubscribe()
  }

  private fetchIsResolutedFlag(): Observable<
    ListResponse<ValidatedAddress, AddressListResponseMeta>
  > {
    return this.addressRepository
      .getBranchAddresses(this.id, {
        limit: 1,
        page: 1,
        filter: [
          {
            name: 'isResoluted',
            val: false,
            op: 'eq',
          },
        ],
      })
      .pipe(tap((response) => (this.openConflicts = response.meta.totalCount)))
  }

  private fetchBranchDetails(): void {
    this.branchSubscription = this.branchRepository
      .getById(this.id)
      .subscribe((res) => {
        this.branch = {
          ...res.data.attributes,
          id: res.data.id,
        }
        if (
          ADDRESS_VALIDATION_IN_PROGRESS_STATES.includes(
            this.branch.processState
          )
        ) {
          this.startValidationCheck()
        } else if (this.branch.state === 'WORKFLOW_MERGING') {
          this.syncSubscription = this.waitForState(
            'state',
            'WORKFLOW_FINISHED'
          ).subscribe(() => {
            this.branch!.state = 'WORKFLOW_FINISHED'
          })
        }
      })
  }

  public onMerge(): void {
    this.isLoading = true
    this.branchRepository.merge(this.id).subscribe((res) => {
      if (res.mergeStatus === 'FAILED') {
        this.notificationService.error(
          this.translateService.instant(
            'pages.clearingDetail.apply.notification.error'
          ),
          res.message
        )
      } else {
        this.branch!.state = 'WORKFLOW_MERGING'
        this.notificationService.success(
          this.translateService.instant(
            'pages.clearingDetail.apply.notification.start'
          ),
          ''
        )
        this.syncSubscription = this.waitForState(
          'state',
          'WORKFLOW_FINISHED'
        ).subscribe(() => {
          this.notificationService.success(
            this.translateService.instant(
              'pages.clearingDetail.apply.notification.success'
            ),
            ''
          )
          this.router.navigate(['clearings'])
        })
      }
    })
  }

  public startReValidation(): void {
    this.branch!.processState = 'ADDRESS_VALIDATION_STARTED'
    this.isValidating = true
    this.branchRepository.sync(this.id).subscribe(() => {
      this.startValidationCheck()
    })
  }

  private startValidationCheck(): void {
    this.isValidating = true
    this.syncSubscription = this.waitForState(
      'processState',
      'ADDRESS_VALIDATION_COMPLETED'
    ).subscribe(() => {
      location.reload()
    })
  }

  private waitForState(
    stateType: 'state',
    status: BranchStatus
  ): Observable<BranchStateResponse>
  private waitForState(
    stateType: 'processState',
    status: BranchProgressState
  ): Observable<BranchStateResponse>
  private waitForState(
    stateType: 'state' | 'processState',
    status: BranchStatus | BranchProgressState
  ): Observable<BranchStateResponse> {
    return interval(1000).pipe(
      startWith(),
      exhaustMap(() => {
        return this.branchRepository.getState(this.id)
      }),
      takeWhile((res) => res[stateType] !== status),
      last()
    )
  }
}
